import React, { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { supabase } from '../lib/supabase';
import type { Profile } from '../lib/supabase';
import { supabaseService } from '../services/SupabaseService';
import type { User, AuthError } from '@supabase/supabase-js';

interface SupabaseAuthContextType {
  user: User | null;
  profile: Profile | null;
  isAuthenticated: boolean;
  loading: boolean;
  signUp: (email: string, password: string, firstName: string, lastName: string) => Promise<{ user: User | null; error: AuthError | null }>;
  signIn: (email: string, password: string) => Promise<{ user: User | null; error: AuthError | null }>;
  signOut: () => Promise<void>;
  updateProfile: (updates: Partial<Profile>) => Promise<Profile | null>;
  refreshProfile: () => Promise<void>;
}

const SupabaseAuthContext = createContext<SupabaseAuthContextType | undefined>(undefined);

export const useSupabaseAuth = () => {
  const context = useContext(SupabaseAuthContext);
  if (context === undefined) {
    throw new Error('useSupabaseAuth must be used within a SupabaseAuthProvider');
  }
  return context;
};

interface SupabaseAuthProviderProps {
  children: ReactNode;
}

export const SupabaseAuthProvider: React.FC<SupabaseAuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;

    // Get initial session
    const getInitialSession = async () => {
      try {
        const { data: { session }, error } = await supabase.auth.getSession();
        
        if (error) {
          console.error('Error getting session:', error);
          if (mounted) setLoading(false);
          return;
        }

        if (mounted) {
          setUser(session?.user ?? null);
          if (session?.user) {
            await loadProfile(session.user.id);
          } else {
            setLoading(false);
          }
        }
      } catch (error) {
        console.error('Error in getInitialSession:', error);
        if (mounted) setLoading(false);
      }
    };

    getInitialSession();

    // Listen for auth changes
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange(async (event, session) => {
      console.log('Auth state changed:', event, session?.user?.email);
      
      if (mounted) {
        setUser(session?.user ?? null);
        if (session?.user) {
          await loadProfile(session.user.id);
        } else {
          setProfile(null);
          setLoading(false);
        }
      }
    });

    return () => {
      mounted = false;
      subscription.unsubscribe();
    };
  }, []);

  const loadProfile = async (userId: string) => {
    try {
      console.log('Loading profile for user:', userId);
      
      const { data: userProfile, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('user_id', userId)
        .maybeSingle();

      if (error) {
        console.error('Error loading profile:', error);
        // Don't throw error, just log it
      }

      console.log('Profile loaded:', userProfile);
      setProfile(userProfile);
    } catch (error) {
      console.error('Error in loadProfile:', error);
    } finally {
      setLoading(false);
    }
  };

  const signUp = async (email: string, password: string, firstName: string, lastName: string) => {
    try {
      console.log('Signing up user:', email);
      
      const { data, error } = await supabase.auth.signUp({
        email: email.toLowerCase().trim(),
        password,
        options: {
          data: {
            first_name: firstName,
            last_name: lastName,
          }
        }
      });

      if (error) {
        console.error('Signup error:', error);
        return { user: null, error };
      }

      // Create profile in database
      if (data.user) {
        console.log('Creating profile for new user:', data.user.id);
        
        try {
          const profileData: Partial<Profile> = {
            user_id: data.user.id,
            pi_username: '',
            first_name: firstName.trim(),
            last_name: lastName.trim(),
            email: email.toLowerCase().trim(),
            age: 25,
            gender: 'other',
            bio: '',
            interests: [],
            photos: [],
            location_city: '',
            location_country: '',
            latitude: 0,
            longitude: 0,
            looking_for: [],
            relationship_type: 'casual',
            min_age: 18,
            max_age: 50,
            max_distance: 50,
            is_active: true,
            is_premium: false,
            is_verified: false,
            is_pi_pioneer: false
          };

          const newProfile = await supabaseService.createProfile(profileData);
          console.log('Profile created successfully:', newProfile);
          setProfile(newProfile);
          
          // Create default user settings
          await supabaseService.createUserSettings(data.user.id);
          
        } catch (profileError) {
          console.error('Error creating profile:', profileError);
          // Continue with auth even if profile creation fails
        }
      }

      return { user: data.user, error: null };
    } catch (error) {
      console.error('Signup error:', error);
      return { user: null, error: error as AuthError };
    }
  };

  const signIn = async (email: string, password: string) => {
    try {
      console.log('Signing in user:', email);
      
      const { data, error } = await supabase.auth.signInWithPassword({
        email: email.toLowerCase().trim(),
        password,
      });

      if (error) {
        console.error('Signin error:', error);
      } else {
        console.log('Signin successful:', data.user?.email);
      }

      return { user: data.user, error };
    } catch (error) {
      console.error('Signin error:', error);
      return { user: null, error: error as AuthError };
    }
  };

  const signOut = async () => {
    try {
      console.log('Signing out user');
      await supabase.auth.signOut();
      setProfile(null);
      console.log('Signout successful');
    } catch (error) {
      console.error('Signout error:', error);
    }
  };

  const updateProfile = async (updates: Partial<Profile>): Promise<Profile | null> => {
    if (!user) {
      console.error('No user found for profile update');
      return null;
    }
    
    try {
      console.log('Updating profile for user:', user.id, updates);
      
      const { data: updatedProfile, error } = await supabase
        .from('profiles')
        .update({
          ...updates,
          updated_at: new Date().toISOString()
        })
        .eq('user_id', user.id)
        .select()
        .single();

      if (error) {
        console.error('Error updating profile:', error);
        return null;
      }

      if (updatedProfile) {
        setProfile(updatedProfile);
        console.log('Profile updated successfully');
      }
      return updatedProfile;
    } catch (error) {
      console.error('Error in updateProfile:', error);
      return null;
    }
  };

  const refreshProfile = async () => {
    if (!user) return;
    console.log('Refreshing profile for user:', user.id);
    await loadProfile(user.id);
  };

  const value: SupabaseAuthContextType = {
    user,
    profile,
    isAuthenticated: !!user && !!profile,
    loading,
    signUp,
    signIn,
    signOut,
    updateProfile,
    refreshProfile,
  };

  return (
    <SupabaseAuthContext.Provider value={value}>
      {children}
    </SupabaseAuthContext.Provider>
  );
};